/**
 * @import { ItineraryDayGroup } from '../../types/itinerary-day-group'
 */
import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useMemo, useRef } from 'react';
import { usePortInfoContext } from '../../context/port-info';
import { useCruise } from '../../hooks/use-cruise';
import { useDay } from '../../hooks/use-day';
import { getItineraryDayTrackingAttributes } from '../../utils/analytics';
import { formatPortName, groupItineraryDays } from '../../utils/cruise';
import classes from './day-list.module.css';

/**
 * @returns {React.JSX.Element}
 */
export function DayList() {
  const { data: cruise } = useCruise();
  const { viewDay } = useDay();
  const days = useMemo(() => groupItineraryDays(cruise), [cruise]);
  const map = useMap();
  const { portInfo } = usePortInfoContext();
  const contentRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const selectedDayNumber = portInfo?.days[portInfo.selectedDayIndex]?.dayNumber;

  /**
   * @param {ItineraryDayGroup} day
   */
  const handleDayClick = (day) => {
    const portPositions = day.ports
      .map((port) => port.position)
      .filter((position) => position != null)
      .map((position) => ({ lng: position[0], lat: position[1] }));
    const mapBounds = map && map.getBounds();
    if (mapBounds && portPositions.length && !portPositions.some((position) => mapBounds.contains(position))) {
      const bounds = new google.maps.LatLngBounds();
      portPositions.forEach((position) => bounds.extend(position));
      map.setCenter(bounds.getCenter());
    }
    viewDay(day.dayNumber);
  };

  useEffect(() => {
    if (!selectedDayNumber) return;

    requestAnimationFrame(() => {
      const element = contentRef.current?.querySelector(`[data-day-number="${selectedDayNumber}"]`);
      if (!element) return;

      // Find scrollable parent
      let container = contentRef.current?.parentElement;
      while (container && getComputedStyle(container).overflowY !== 'auto') {
        container = container.parentElement;
      }

      if (container) {
        const offset = 82;
        const relativeTop = element.getBoundingClientRect().top - container.getBoundingClientRect().top;
        container.scrollTop += relativeTop - offset;
      }
    });
  }, [selectedDayNumber]);

  return (
    <div ref={contentRef} className={classes.dayList}>
      {days.map((day, dayIndex) => (
        <div
          key={dayIndex}
          data-day-number={day.dayNumber}
          className={`${classes.day} ${selectedDayNumber === day.dayNumber ? classes.dayHighlight : ''}`}
        >
          {day.ports.map((port, portIndex) => {
            const name = formatPortName(port);
            return (
              <button
                key={portIndex}
                type="button"
                className={classes.btnPort}
                onClick={() => handleDayClick(day)}
                {...getItineraryDayTrackingAttributes(name)}
              >
                <span className={classes.port}>
                  <span className={classes.image}>
                    <img src={port.image.url} loading="lazy" alt="" />
                  </span>
                  <span className={classes.dayDetail}>
                    {portIndex === 0 && <h1 className={classes.dayTitle}>Day {day.dayNumber}</h1>}
                    <h2 className={classes.daySubtitle}>{name}</h2>
                    <span className={classes.dayDescription}>{port.description}</span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
